/**
 * layout/presets.js - Section Layout Preset Definitions
 * Location: /app/frontend/src/admin/page_builder/layout/
 * 
 * Defines section-level layout presets that control how content
 * is arranged within a section. These are NOT per-element positioning.
 * 
 * Layout presets:
 * - single: Full-width single column (default)
 * - 50-50: Two equal columns
 * - 60-40: Two columns, left wider
 * - 40-60: Two columns, right wider
 * - media-left: Media on left, content on right
 * - media-right: Content on left, media on right
 * - overlay: Content overlaid on background media
 */

/**
 * Layout preset type constants
 */
export const LayoutPreset = {
  SINGLE: 'single',
  SPLIT_50_50: '50-50',
  SPLIT_60_40: '60-40',
  SPLIT_40_60: '40-60',
  MEDIA_LEFT: 'media-left',
  MEDIA_RIGHT: 'media-right',
  OVERLAY: 'overlay',
};

/**
 * Human-readable labels for layout presets
 */
export const LayoutPresetLabel = {
  [LayoutPreset.SINGLE]: 'Single Column',
  [LayoutPreset.SPLIT_50_50]: '50/50 Split',
  [LayoutPreset.SPLIT_60_40]: '60/40 Split',
  [LayoutPreset.SPLIT_40_60]: '40/60 Split',
  [LayoutPreset.MEDIA_LEFT]: 'Media Left',
  [LayoutPreset.MEDIA_RIGHT]: 'Media Right',
  [LayoutPreset.OVERLAY]: 'Overlay',
};

/**
 * Layout preset icons (for UI selector)
 */
export const LayoutPresetIcon = {
  [LayoutPreset.SINGLE]: 'square',
  [LayoutPreset.SPLIT_50_50]: 'columns',
  [LayoutPreset.SPLIT_60_40]: 'panel-left',
  [LayoutPreset.SPLIT_40_60]: 'panel-right',
  [LayoutPreset.MEDIA_LEFT]: 'image',
  [LayoutPreset.MEDIA_RIGHT]: 'image',
  [LayoutPreset.OVERLAY]: 'layers',
};

/**
 * CSS grid/flex definitions for each layout preset
 */
export const LayoutPresetStyles = {
  [LayoutPreset.SINGLE]: {
    display: 'block',
    gridTemplateColumns: null,
    description: 'Full-width single column layout',
  },
  [LayoutPreset.SPLIT_50_50]: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 'var(--tc-spacing-6, 1.5rem)',
    description: 'Two equal-width columns',
  },
  [LayoutPreset.SPLIT_60_40]: {
    display: 'grid',
    gridTemplateColumns: '3fr 2fr',
    gap: 'var(--tc-spacing-6, 1.5rem)',
    description: 'Left column wider (60%)',
  },
  [LayoutPreset.SPLIT_40_60]: {
    display: 'grid',
    gridTemplateColumns: '2fr 3fr',
    gap: 'var(--tc-spacing-6, 1.5rem)',
    description: 'Right column wider (60%)',
  },
  [LayoutPreset.MEDIA_LEFT]: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 'var(--tc-spacing-6, 1.5rem)',
    mediaSlot: 'left',
    description: 'Media on left, content on right',
  },
  [LayoutPreset.MEDIA_RIGHT]: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 'var(--tc-spacing-6, 1.5rem)',
    mediaSlot: 'right',
    description: 'Content on left, media on right',
  },
  [LayoutPreset.OVERLAY]: {
    display: 'block',
    position: 'relative',
    description: 'Content overlaid on background media',
  },
};

/**
 * Responsive breakpoints for layout presets
 * At mobile widths, most layouts collapse to single column
 */
export const LayoutResponsiveBreakpoints = {
  mobile: 640,  // Below this, all layouts become single column
  tablet: 768,  // Tablet breakpoint
  desktop: 1024, // Desktop breakpoint
};

/**
 * Get CSS styles for a layout preset
 * @param {string} preset - Layout preset key
 * @param {string} [viewport='desktop'] - Current viewport
 * @returns {Object} CSS style object
 */
export function getLayoutStyles(preset, viewport = 'desktop') {
  const layoutDef = LayoutPresetStyles[preset] || LayoutPresetStyles[LayoutPreset.SINGLE];
  
  // For mobile, collapse to single column
  if (viewport === 'mobile') {
    return {
      display: 'block',
    };
  }
  
  const styles = {};
  
  if (layoutDef.display) {
    styles.display = layoutDef.display;
  }
  
  if (layoutDef.gridTemplateColumns) {
    styles.gridTemplateColumns = layoutDef.gridTemplateColumns;
  }
  
  if (layoutDef.gap) {
    styles.gap = layoutDef.gap;
  }
  
  if (layoutDef.position) {
    styles.position = layoutDef.position;
  }
  
  return styles;
}

/**
 * Get all available layout presets as array for UI selectors
 * @returns {Array<{value: string, label: string, icon: string, description: string}>}
 */
export function getLayoutPresetOptions() {
  return Object.values(LayoutPreset).map(preset => ({
    value: preset,
    label: LayoutPresetLabel[preset],
    icon: LayoutPresetIcon[preset],
    description: LayoutPresetStyles[preset]?.description || '',
  }));
}

/**
 * Check if a layout preset has a media slot
 * @param {string} preset - Layout preset key
 * @returns {boolean}
 */
export function hasMediaSlot(preset) {
  const layoutDef = LayoutPresetStyles[preset];
  return !!layoutDef?.mediaSlot;
}

/**
 * Get the media slot position for a layout preset
 * @param {string} preset - Layout preset key
 * @returns {'left'|'right'|null}
 */
export function getMediaSlotPosition(preset) {
  const layoutDef = LayoutPresetStyles[preset];
  return layoutDef?.mediaSlot || null;
}

/**
 * Default layout configuration for new sections
 */
export const DEFAULT_LAYOUT = {
  preset: LayoutPreset.SINGLE,
};

export default {
  LayoutPreset,
  LayoutPresetLabel,
  LayoutPresetIcon,
  LayoutPresetStyles,
  LayoutResponsiveBreakpoints,
  getLayoutStyles,
  getLayoutPresetOptions,
  hasMediaSlot,
  getMediaSlotPosition,
  DEFAULT_LAYOUT,
};
